import React from 'react';
import { motion } from 'framer-motion';
import { motionPresets } from '../../lib/motion';

const Button = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  icon = null,
  onClick,
  animate = true,
  ...props 
}) => {
  const variants = {
    primary: `
      bg-gradient-to-r from-[#d8c6a1] to-[#c7b28e]
      text-[#3c3427] shadow-sm hover:shadow-md
      border border-[#cbb792]
    `,
    secondary: `
      bg-[#f2e7d3]
      text-[#3f382c] border border-[#d7c7a6]
      hover:bg-[#e9dcc1]
    `,
    accent: `
      bg-gradient-to-r from-[#e1cbb1] to-[#d2b799]
      text-[#3c3427] shadow-sm hover:shadow-md
      border border-[#cdb99c]
    `,
    ghost: `
      glass backdrop-blur-md border border-[#d8c6a7]/60
      text-[#5b5142] hover:text-[#3f382c] hover:bg-[#f0e4cd]/60
    `,
    outline: `
      border-2 border-[#cbb792] bg-transparent
      text-[#6b5e4a] hover:bg-[#efe3cb] hover:border-[#bfa87f]
    `,
    danger: `
      bg-gradient-to-r from-[#c96b5b] to-[#b25a4a]
      text-white shadow-[#c96b5b]/30 hover:shadow-[#c96b5b]/50
      border-0
    `
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    xl: 'px-8 py-4 text-xl'
  };

  const Component = animate ? motion.button : 'button';

  const motionProps = animate ? {
    ...motionPresets.tapScale,
    whileHover: { y: -2 },
    whileTap: { y: 0, scale: 0.95 }
  } : {};

  return (
    <Component
      className={`
        rounded-glass font-semibold
        transition-all duration-120 ease-out
        focus:outline-none focus:ring-2 focus:ring-[#d1c0a3]/60 focus:ring-offset-2 focus:ring-offset-[#f6f1e8]
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
        relative inline-flex items-center justify-center gap-2
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      disabled={disabled || loading}
      onClick={onClick}
      {...motionProps}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      
      <span className={`flex items-center gap-2 ${loading ? 'opacity-0' : 'opacity-100'}`}>
        {icon && <span className="w-4 h-4">{icon}</span>}
        {children}
      </span>
    </Component>
  );
};

export default Button;
